/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { NextResponse } from "next/server";

import type { NextRequest } from "next/server";

/** Query param the auth proxy sets when the OAuth callback fails. */
const AUTH_ERROR_CODE_PARAM = "auth_error_code";
/** Companion param carrying the human-readable reason. Logged, never rendered. */
const AUTH_ERROR_DESCRIPTION_PARAM = "auth_error_description";

/** Cap on each logged param. Both are unauthenticated, unbounded query-string values. */
const MAX_LOGGED_PARAM_LENGTH = 200;

/**
 * Serves the site's own 500 page when a login attempt came back with an error.
 *
 * When the OAuth callback fails, the auth worker redirects to the original `returnPath`
 * with `auth_error_code` and `auth_error_description` appended
 * (`services/auth-worker/src/callback.ts` in `online-identity/cloudflare-auth-proxy`).
 * The landing page is whatever the caller asked to return to, so this cannot live in a
 * single route — the query string is only visible to middleware, which sees every request.
 *
 * The code is an open-ended string: three values are hardcoded in the worker
 * (`invalid_state`, `missing_code`, `unknown`) and the rest are passed straight through
 * from Account Service or the OAuth `error` param. No enum exists to map against, so every
 * value gets the same generic 500 page rather than a guessed message. Both params are
 * logged so the real code set becomes observable and a specific treatment can be added
 * later on evidence.
 *
 * Rewrites rather than redirects, so the browser keeps the URL it asked for and the failure
 * costs no extra round trip. The query string is dropped from the rewrite target: leaving it
 * on would re-trigger this check if the error page itself were ever routed back through
 * middleware, and the error params have no meaning to the page being rendered.
 *
 * @param request - Incoming Next.js edge request.
 * @param errorPath - App-relative pathname of the site's 500 page. Sites with a `[lang]`
 *                    route segment must pass a locale-prefixed path (e.g. `/en-US/error-500`);
 *                    a bare `/error-500` would be captured by `[lang]` as `lang="error-500"`.
 * @param init - Optional request init forwarded to `NextResponse.rewrite`, for sites whose
 *               error page needs request headers (e.g. `x-cosmos-site-id`) to render.
 * @returns A rewrite `NextResponse` when the error param is present, otherwise `undefined`.
 */
export function processAuthError(
	request: NextRequest,
	errorPath: string,
	init?: { request: { headers: Headers } },
): NextResponse | undefined {
	const { searchParams } = request.nextUrl;

	const code = searchParams.get(AUTH_ERROR_CODE_PARAM);
	if (!code) return undefined;

	/*
	  Log the pair, not the URL — the rest of the query string belongs to the page the user was
	  returning to and may carry unrelated caller data.

	  Both values are truncated because both arrive unauthenticated in the query string and are
	  otherwise unbounded: anyone can hit any path with a multi-kilobyte `auth_error_description`
	  and have it written to logs on every request. The cap is well clear of the real values,
	  which are short Account Service codes and one-line messages.
	*/
	console.warn("auth callback returned an error, serving the 500 page", {
		authErrorCode: code.slice(0, MAX_LOGGED_PARAM_LENGTH),
		authErrorDescription: (searchParams.get(AUTH_ERROR_DESCRIPTION_PARAM) ?? "").slice(0, MAX_LOGGED_PARAM_LENGTH),
		pathname: request.nextUrl.pathname,
	});

	const url = request.nextUrl.clone();
	url.pathname = errorPath;
	url.search = "";

	return init ? NextResponse.rewrite(url, init) : NextResponse.rewrite(url);
}
